import { assessTranscript, type TranscriptInput } from './transcript-gate'

/**
 * Release half of push-to-talk. The key gesture fires its release whether or
 * not the hold actually started listening (the hold start is gated on the
 * companion being enabled, the release is not), so the caller passes whether
 * THIS push-to-talk started recording; a release without a start is a no-op.
 * Never screenshots here: CompanionManager.sendMessage captures only when the
 * turn needs the image. Unintelligible transcripts (noise, wrong-language
 * garble, low STT confidence) are dropped like an empty one: no send, no reply.
 */
export async function finishPushToTalk(opts: {
  wasListening: boolean
  stopRecorder: () => void
  stopTranscription: () => Promise<{
    text: string
    language?: string
    detectedLanguage?: string
    confidence?: number
  }>
  /**
   * The app's chosen companion language; omit to skip the language rule. A
   * function is read AFTER the transcript arrives, so a host that changes the
   * language from what was heard (a Spanish question) is respected.
   */
  expectedLanguage?: string | (() => string | undefined)
  /**
   * Called with the transcript before the gate, so the host can react to what
   * was heard. A throw here is logged and never loses the transcript.
   */
  onHeard?: (text: string) => void
  onTranscript: (text: string) => Promise<void>
  onEmpty: () => void
}): Promise<void> {
  if (!opts.wasListening) return

  opts.stopRecorder()
  const { text, detectedLanguage, confidence } = await opts.stopTranscription()
  try {
    opts.onHeard?.(text)
  } catch (err) {
    console.warn('[voice] onHeardText failed:', err)
  }
  const input: TranscriptInput = {
    text,
    detectedLanguage,
    confidence,
    expectedLanguage:
      typeof opts.expectedLanguage === 'function' ? opts.expectedLanguage() : opts.expectedLanguage,
  }
  const verdict = assessTranscript(input)
  if (verdict.ok) {
    await opts.onTranscript(text)
  } else {
    if (verdict.reason !== 'empty') {
      console.debug('[voice] dropped transcript:', verdict.reason, JSON.stringify(text))
    }
    opts.onEmpty()
  }
}
